import React, { useEffect, useState } from 'react';
import {
  Loader2, BarChart3, ShieldCheck, ShieldX, Building2, MapPin,
  AlertTriangle, Database, PieChart, TrendingUp, Briefcase, Info,
  Heart, Scale, Truck, Calculator, Users, Zap, ArrowRight, CheckCircle,
} from 'lucide-react';
import { IntelligenceLayout, fetchWithAuth, MetricCard } from './IntelligenceLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NICHE_ICONS = {
  accounting_finance: Calculator,
  healthcare: Heart,
  legal: Scale,
  logistics: Truck,
};

const NICHE_COLORS = {
  accounting_finance: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', bar: 'bg-emerald-500' },
  healthcare: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', bar: 'bg-blue-500' },
  legal: { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', bar: 'bg-violet-500' },
  logistics: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', bar: 'bg-orange-500' },
};

const BarCell = ({ value, max, color = 'bg-[#0A1F44]' }) => {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-medium text-gray-600 w-10 text-right">{value}</span>
    </div>
  );
};

const PctBadge = ({ value, total }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  let color = 'bg-emerald-50 text-emerald-700';
  if (pct < 30) color = 'bg-red-50 text-red-700';
  else if (pct < 60) color = 'bg-amber-50 text-amber-700';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${color}`}>
      {pct}%
    </span>
  );
};

export default function CoverageDiagnostics({ embedded = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrichingCompany, setEnrichingCompany] = useState(null);
  const [batchEnriching, setBatchEnriching] = useState(false);
  const [batchResult, setBatchResult] = useState(null);

  const loadData = () => {
    setLoading(true);
    fetchWithAuth(`${API_URL}/api/intelligence/coverage-diagnostics`)
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  const handleEnrichOne = async (companyName) => {
    setEnrichingCompany(companyName);
    try {
      await fetchWithAuth(`${API_URL}/api/intelligence/enrichment-priorities/${encodeURIComponent(companyName)}/enrich`, { method: 'POST' });
      loadData();
    } catch {} finally { setEnrichingCompany(null); }
  };

  const handleBatchEnrich = async () => {
    setBatchEnriching(true);
    setBatchResult(null);
    try {
      const r = await fetchWithAuth(`${API_URL}/api/intelligence/enrichment-priorities/batch-enrich?max_count=10`, { method: 'POST' });
      setBatchResult(r);
      loadData();
    } catch {} finally { setBatchEnriching(false); }
  };

  const qual = data?.qualification_summary || {};
  const totalComp = (qual.qualified || 0) + (qual.excluded || 0) + (qual.manual_override || 0);
  const sizes = data?.size_band_distribution || {};
  const enrichment = data?.enrichment_coverage || {};
  const niches = data?.niche_coverage || {};
  const topIndustries = data?.top_industries || [];
  const topStates = data?.top_states || [];
  const exclusions = data?.exclusion_breakdown || {};
  const epData = data?.enrichment_priority || {};

  const maxIndustry = topIndustries.reduce((m, i) => Math.max(m, i.total), 0);
  const maxState = topStates.reduce((m, s) => Math.max(m, s.total), 0);

  return (
    <IntelligenceLayout title="Coverage Diagnostics" embedded={embedded}>
      <header className="bg-white border-b border-gray-200 px-8 py-5 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-amber-50 rounded-xl flex items-center justify-center">
            <BarChart3 className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#0A1F44]" data-testid="coverage-diagnostics-title">Coverage Diagnostics</h1>
            <p className="text-sm text-gray-500">Where the system is thin — by niche, industry, geography, size, and enrichment</p>
          </div>
        </div>
      </header>

      <main className="p-8 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-20 gap-2 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" /> Analyzing coverage gaps...
          </div>
        ) : (
          <>
            {/* Top-level KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4" data-testid="coverage-metrics">
              <MetricCard label="Active Postings" value={(data?.total_active_postings || 0).toLocaleString()} icon={Database} subtext="Total ingested" />
              <MetricCard label="Companies Tracked" value={(data?.total_companies_ingested || 0).toLocaleString()} icon={Building2} subtext="Unique names" />
              <MetricCard label="Qualified Targets" value={qual.qualified || 0} icon={ShieldCheck} subtext={`${totalComp > 0 ? Math.round((qual.qualified / totalComp) * 100) : 0}% of evaluated`} />
              <MetricCard label="Strategic Enterprise" value={qual.strategic_enterprise || 0} icon={TrendingUp} subtext="Hidden by default" />
              <MetricCard label="Excluded" value={qual.excluded || 0} icon={ShieldX} subtext={`${totalComp > 0 ? Math.round((qual.excluded / totalComp) * 100) : 0}% filtered`} />
              <MetricCard label="Enrichment Priority" value={epData.total_flagged || 0} icon={Zap} subtext={`${epData.pending_enrichment || 0} pending`} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Qualification Funnel */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-testid="qual-funnel">
                <h3 className="text-sm font-semibold text-[#0A1F44] uppercase tracking-wider mb-4 flex items-center gap-2">
                  <PieChart className="h-4 w-4 text-gray-400" /> Qualification Funnel
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Qualified</span>
                    <div className="flex items-center gap-2">
                      <div className="w-48 h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${totalComp > 0 ? (qual.qualified / totalComp) * 100 : 0}%` }} />
                      </div>
                      <span className="text-sm font-bold text-[#0A1F44] w-12 text-right">{qual.qualified || 0}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Excluded</span>
                    <div className="flex items-center gap-2">
                      <div className="w-48 h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-red-500 rounded-full transition-all" style={{ width: `${totalComp > 0 ? (qual.excluded / totalComp) * 100 : 0}%` }} />
                      </div>
                      <span className="text-sm font-bold text-[#0A1F44] w-12 text-right">{qual.excluded || 0}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Strategic Enterprise</span>
                    <div className="flex items-center gap-2">
                      <div className="w-48 h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-indigo-500 rounded-full transition-all" style={{ width: `${totalComp > 0 ? ((qual.strategic_enterprise || 0) / totalComp) * 100 : 0}%` }} />
                      </div>
                      <span className="text-sm font-bold text-[#0A1F44] w-12 text-right">{qual.strategic_enterprise || 0}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Manual Override</span>
                    <div className="flex items-center gap-2">
                      <div className="w-48 h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-violet-500 rounded-full transition-all" style={{ width: `${totalComp > 0 ? ((qual.manual_override || 0) / totalComp) * 100 : 0}%` }} />
                      </div>
                      <span className="text-sm font-bold text-[#0A1F44] w-12 text-right">{qual.manual_override || 0}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Exclusion Reasons */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-testid="exclusion-breakdown">
                <h3 className="text-sm font-semibold text-[#0A1F44] uppercase tracking-wider mb-4 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-400" /> Why Companies Are Excluded
                </h3>
                {Object.keys(exclusions).length > 0 ? (
                  <div className="space-y-2">
                    {Object.entries(exclusions).map(([reason, count]) => (
                      <div key={reason} className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 capitalize">{reason.replace(/-/g, ' ')}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-32 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-red-400 rounded-full" style={{ width: `${(count / (qual.excluded || 1)) * 100}%` }} />
                          </div>
                          <span className="text-xs font-medium text-gray-600 w-8 text-right">{count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-xs text-gray-400">No exclusions recorded</p>}
              </div>

              {/* Size Band Distribution */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-testid="size-distribution">
                <h3 className="text-sm font-semibold text-[#0A1F44] uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Users className="h-4 w-4 text-gray-400" /> Size Band Distribution
                </h3>
                <div className="space-y-3">
                  {[
                    { key: 'mid_market', label: 'Mid-Market (50-1K)', color: 'bg-emerald-500' },
                    { key: 'growth', label: 'Growth (1K-5K)', color: 'bg-blue-500' },
                    { key: 'large', label: 'Large (5K+)', color: 'bg-amber-500' },
                    { key: 'small', label: 'Small (<50)', color: 'bg-gray-400' },
                    { key: 'unknown', label: 'Unknown', color: 'bg-gray-300' },
                  ].map(s => (
                    <div key={s.key} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">{s.label}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-40 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${s.color}`} style={{ width: `${totalComp > 0 ? ((sizes[s.key] || 0) / totalComp) * 100 : 0}%` }} />
                        </div>
                        <span className="text-xs font-medium text-gray-600 w-10 text-right">{sizes[s.key] || 0}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Enrichment Coverage */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-testid="enrichment-coverage">
                <h3 className="text-sm font-semibold text-[#0A1F44] uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Database className="h-4 w-4 text-gray-400" /> Enrichment Coverage
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-emerald-50/50 rounded-xl border border-emerald-100">
                    <p className="text-3xl font-bold text-emerald-700">{enrichment.with_employee_count || 0}</p>
                    <p className="text-xs text-gray-500 mt-1">With Employee Count</p>
                    <PctBadge value={enrichment.with_employee_count || 0} total={(enrichment.with_employee_count || 0) + (enrichment.without || 0)} />
                  </div>
                  <div className="text-center p-4 bg-red-50/50 rounded-xl border border-red-100">
                    <p className="text-3xl font-bold text-red-700">{enrichment.without || 0}</p>
                    <p className="text-xs text-gray-500 mt-1">Missing Employee Data</p>
                    <PctBadge value={enrichment.without || 0} total={(enrichment.with_employee_count || 0) + (enrichment.without || 0)} />
                  </div>
                </div>
                <div className="mt-3 flex items-start gap-2 text-[10px] text-gray-400">
                  <Info className="h-3 w-3 mt-0.5 shrink-0" />
                  Missing employee data means the system can't properly size-band the company. Enriching via Apollo improves classification accuracy.
                </div>
              </div>
            </div>

            {/* Enrichment Priority Workflow */}
            <div className="bg-white border border-amber-200 rounded-xl p-5 shadow-sm" data-testid="enrichment-priority-section">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-[#0A1F44] uppercase tracking-wider flex items-center gap-2">
                  <Zap className="h-4 w-4 text-amber-500" /> Enrichment Priority Queue
                </h3>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500">{epData.total_flagged || 0} flagged | {epData.already_enriched || 0} enriched | {epData.pending_enrichment || 0} pending</span>
                  <button onClick={handleBatchEnrich} disabled={batchEnriching || (epData.pending_enrichment || 0) === 0}
                    data-testid="batch-enrich-btn"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors disabled:opacity-50">
                    {batchEnriching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
                    {batchEnriching ? 'Enriching...' : 'Batch Enrich Top 10'}
                  </button>
                </div>
              </div>

              {batchResult && (
                <div className="mb-4 p-3 bg-emerald-50 rounded-lg border border-emerald-100 text-sm text-emerald-800" data-testid="batch-result">
                  <CheckCircle className="inline h-4 w-4 mr-1" />
                  Batch complete: {batchResult.success} enriched, {batchResult.no_data} no data found, {batchResult.errors} errors. {batchResult.remaining_pending} still pending.
                </div>
              )}

              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="text-center p-3 bg-amber-50/50 rounded-xl border border-amber-100">
                  <p className="text-2xl font-bold text-amber-700">{epData.total_flagged || 0}</p>
                  <p className="text-[10px] text-gray-500">Flagged for Enrichment</p>
                </div>
                <div className="text-center p-3 bg-emerald-50/50 rounded-xl border border-emerald-100">
                  <p className="text-2xl font-bold text-emerald-700">{epData.already_enriched || 0}</p>
                  <p className="text-[10px] text-gray-500">Successfully Enriched</p>
                </div>
                <div className="text-center p-3 bg-blue-50/50 rounded-xl border border-blue-100">
                  <p className="text-2xl font-bold text-blue-700">{epData.pending_enrichment || 0}</p>
                  <p className="text-[10px] text-gray-500">Pending Enrichment</p>
                </div>
              </div>

              {(epData.top_companies || []).length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-gray-500 uppercase border-b border-gray-100 bg-gray-50/50">
                        <th className="text-left px-3 py-2">Company</th>
                        <th className="text-left px-3 py-2">Tags</th>
                        <th className="text-left px-3 py-2">Best Niche</th>
                        <th className="text-right px-3 py-2">Fit</th>
                        <th className="text-right px-3 py-2">Score</th>
                        <th className="text-center px-3 py-2">Risk</th>
                        <th className="text-left px-3 py-2">Why Flagged</th>
                        <th className="text-center px-3 py-2">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(epData.top_companies || []).slice(0, 15).map((c, i) => (
                        <tr key={c.company} className="border-t border-gray-100 hover:bg-amber-50/30 transition-colors" data-testid={`ep-row-${i}`}>
                          <td className="px-3 py-2">
                            <div className="font-medium text-[#0A1F44]">{c.company}</div>
                            <div className="text-[10px] text-gray-400">{c.industry || '—'} | {c.total_postings} roles</div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap gap-0.5">
                              {c.tags?.slice(0, 3).map((t, j) => (
                                <span key={j} className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-semibold border ${
                                  t === 'target' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                  t === 'size_unknown' ? 'bg-gray-100 text-gray-500 border-gray-200' :
                                  'bg-gray-50 text-gray-500 border-gray-200'
                                }`}>{t === 'target' ? 'Target' : t === 'size_unknown' ? 'Size Unknown' : t}</span>
                              ))}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-xs text-blue-600 font-medium">{c.best_niche || '—'}</td>
                          <td className="px-3 py-2 text-right">
                            <span className="text-xs font-bold text-[#0A1F44]">{c.outreach_fit || 0}</span>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <span className="inline-flex px-2 py-0.5 rounded text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">{c.priority_score}</span>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-semibold ${
                              c.exclusion_risk === 'high' ? 'bg-red-50 text-red-600 border border-red-200' :
                              c.exclusion_risk === 'medium' ? 'bg-amber-50 text-amber-600 border border-amber-200' :
                              'bg-emerald-50 text-emerald-600 border border-emerald-200'
                            }`}>{c.exclusion_risk}</span>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap gap-1">
                              {c.reasons?.slice(0, 2).map((r, j) => (
                                <span key={j} className="text-[10px] bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded border border-amber-100">{r}</span>
                              ))}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <button onClick={() => handleEnrichOne(c.company)}
                              disabled={enrichingCompany === c.company}
                              data-testid={`enrich-btn-${i}`}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-medium bg-[#0A1F44] text-white hover:bg-[#0A1F44]/90 transition-colors disabled:opacity-50">
                              {enrichingCompany === c.company ? <Loader2 className="h-3 w-3 animate-spin" /> : <ArrowRight className="h-3 w-3" />}
                              Enrich
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="mt-3 flex items-start gap-2 text-[10px] text-gray-400">
                <Info className="h-3 w-3 mt-0.5 shrink-0" />
                Enrichment priorities: qualified unknown-size companies with strong niche fit, outreach alignment, repeated hiring pain, or multi-niche presence. High posting volume alone is not sufficient. "Enrich" triggers Apollo to fetch employee count and firmographic data. After enrichment, the company gets properly size-banded and re-ranked.
              </div>
            </div>

            {/* Strategic Enterprise Section */}
            {(data?.strategic_enterprise?.total || 0) > 0 && (
              <div className="bg-white border border-indigo-200 rounded-xl p-5 shadow-sm" data-testid="strategic-enterprise-section">
                <h3 className="text-sm font-semibold text-[#0A1F44] uppercase tracking-wider mb-4 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-indigo-500" /> Strategic Enterprise Companies
                  <span className="ml-auto text-xs font-normal text-gray-400">{data.strategic_enterprise.total} classified | Hidden from default views</span>
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-gray-500 uppercase border-b border-gray-100 bg-indigo-50/30">
                        <th className="text-left px-3 py-2">Company</th>
                        <th className="text-right px-3 py-2">Roles</th>
                        <th className="text-left px-3 py-2">Industry</th>
                        <th className="text-left px-3 py-2">Best Niche</th>
                        <th className="text-left px-3 py-2">Tags</th>
                        <th className="text-left px-3 py-2">Why Strategic</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.strategic_enterprise.companies || []).slice(0, 15).map((c, i) => (
                        <tr key={c.company} className="border-t border-gray-100 hover:bg-indigo-50/30 transition-colors" data-testid={`se-row-${i}`}>
                          <td className="px-3 py-2 font-medium text-[#0A1F44]">{c.company}</td>
                          <td className="px-3 py-2 text-right font-medium">{c.total_postings}</td>
                          <td className="px-3 py-2 text-xs text-gray-500">{c.industry || '—'}</td>
                          <td className="px-3 py-2 text-xs text-blue-600 font-medium">{c.best_niche || '—'}</td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap gap-0.5">
                              {c.tags?.slice(0, 3).map((t, j) => (
                                <span key={j} className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-semibold border ${
                                  t === 'strategic_enterprise' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' :
                                  t === 'enterprise' ? 'bg-slate-100 text-slate-600 border-slate-200' :
                                  t === 'hard_to_access' ? 'bg-rose-50 text-rose-600 border-rose-200' :
                                  t === 'long_term_opportunity' ? 'bg-cyan-50 text-cyan-700 border-cyan-200' :
                                  'bg-gray-50 text-gray-500 border-gray-200'
                                }`}>{t === 'strategic_enterprise' ? 'Strategic' : t === 'hard_to_access' ? 'Hard to Access' : t === 'long_term_opportunity' ? 'Long-Term' : t}</span>
                              ))}
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap gap-1">
                              {c.reasons?.slice(0, 2).map((r, j) => (
                                <span key={j} className="text-[10px] bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded border border-indigo-100">{r}</span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3 flex items-start gap-2 text-[10px] text-gray-400">
                  <Info className="h-3 w-3 mt-0.5 shrink-0" />
                  Strategic Enterprise: enterprise/oversized companies with real strategic value (strong niche fit, hiring pain, strategic vertical). Not part of default target pipeline. Toggle on in Niche Lanes to view them alongside targets.
                </div>
              </div>
            )}

            {/* Niche Coverage */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-testid="niche-coverage">
              <h3 className="text-sm font-semibold text-[#0A1F44] uppercase tracking-wider mb-4 flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-gray-400" /> Niche Lane Coverage
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {Object.entries(niches).map(([key, val]) => {
                  const colors = NICHE_COLORS[key] || NICHE_COLORS.accounting_finance;
                  const Icon = NICHE_ICONS[key] || Briefcase;
                  return (
                    <div key={key} className={`${colors.bg} rounded-xl p-4 border ${colors.border}`} data-testid={`niche-coverage-${key}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <Icon className={`h-5 w-5 ${colors.text}`} />
                        <span className={`text-sm font-semibold ${colors.text}`}>{val.label}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="text-center">
                          <p className={`text-2xl font-bold ${colors.text}`}>{val.qualified}</p>
                          <p className="text-[10px] text-gray-500">Companies</p>
                        </div>
                        <div className="text-center">
                          <p className={`text-2xl font-bold ${colors.text}`}>{val.roles}</p>
                          <p className="text-[10px] text-gray-500">Matching Roles</p>
                        </div>
                      </div>
                      {val.qualified === 0 && (
                        <div className="mt-2 flex items-center gap-1 text-[10px] text-red-600">
                          <AlertTriangle className="h-3 w-3" /> Thin coverage — may need data expansion
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Industry & State Breakdowns */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Industry */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-testid="industry-breakdown">
                <h3 className="text-sm font-semibold text-[#0A1F44] uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-gray-400" /> By Industry
                </h3>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {topIndustries.map((ind, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm" data-testid={`industry-row-${i}`}>
                      <span className="text-gray-600 w-40 truncate shrink-0" title={ind.industry}>{ind.industry}</span>
                      <div className="flex-1"><BarCell value={ind.qualified} max={maxIndustry} color="bg-emerald-500" /></div>
                      <PctBadge value={ind.qualified} total={ind.total} />
                    </div>
                  ))}
                </div>
              </div>

              {/* State */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-testid="state-breakdown">
                <h3 className="text-sm font-semibold text-[#0A1F44] uppercase tracking-wider mb-4 flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-gray-400" /> By State / Geography
                </h3>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {topStates.map((st, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm" data-testid={`state-row-${i}`}>
                      <span className="text-gray-600 w-16 shrink-0 font-medium">{st.state}</span>
                      <div className="flex-1"><BarCell value={st.qualified} max={maxState} color="bg-blue-500" /></div>
                      <PctBadge value={st.qualified} total={st.total} />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Diagnostic Summary */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-testid="diagnostic-summary">
              <h3 className="text-sm font-semibold text-[#0A1F44] uppercase tracking-wider mb-3">Diagnostic Summary</h3>
              <div className="space-y-2 text-sm text-gray-600">
                {(enrichment.without || 0) > (enrichment.with_employee_count || 0) && (
                  <div className="flex items-start gap-2 bg-amber-50 text-amber-800 px-3 py-2 rounded-lg border border-amber-100">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    <span><strong>Enrichment gap:</strong> {enrichment.without} companies ({Math.round((enrichment.without / ((enrichment.with_employee_count || 0) + (enrichment.without || 0))) * 100)}%) lack employee count data. Apollo enrichment can fill this gap and improve classification accuracy.</span>
                  </div>
                )}
                {(sizes.unknown || 0) > totalComp * 0.3 && (
                  <div className="flex items-start gap-2 bg-amber-50 text-amber-800 px-3 py-2 rounded-lg border border-amber-100">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    <span><strong>Size band gap:</strong> {sizes.unknown || 0} companies have unknown size. Enrichment would help classify them accurately.</span>
                  </div>
                )}
                {Object.values(niches).some(v => v.qualified === 0) && (
                  <div className="flex items-start gap-2 bg-red-50 text-red-800 px-3 py-2 rounded-lg border border-red-100">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    <span><strong>Niche gap:</strong> One or more niche lanes have zero qualified companies. This may indicate insufficient data in that specialty or overly aggressive filtering.</span>
                  </div>
                )}
                {(qual.excluded || 0) > (qual.qualified || 0) && (
                  <div className="flex items-start gap-2 bg-blue-50 text-blue-800 px-3 py-2 rounded-lg border border-blue-100">
                    <Info className="h-4 w-4 shrink-0 mt-0.5" />
                    <span><strong>High exclusion rate:</strong> More companies are excluded ({qual.excluded}) than qualified ({qual.qualified}). This is expected if your data includes many staffing/enterprise/defense companies. Check the exclusion breakdown above to confirm.</span>
                  </div>
                )}
                {(qual.qualified || 0) > 0 && (enrichment.without || 0) < totalComp * 0.2 && (
                  <div className="flex items-start gap-2 bg-emerald-50 text-emerald-800 px-3 py-2 rounded-lg border border-emerald-100">
                    <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" />
                    <span><strong>Good enrichment coverage.</strong> Most companies have employee data. Qualification accuracy is strong.</span>
                  </div>
                )}
              </div>
            </div>

            {/* Info Footer */}
            <div className="flex items-start gap-2 text-xs text-gray-400 px-1">
              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <p>Coverage Diagnostics analyzes the top 1,500 companies with 2+ active postings. Weak result depth can come from: (1) insufficient ingested data, (2) missing enrichment (no employee count), or (3) over-filtering by the qualification pipeline. Use this page to identify which factor dominates.</p>
            </div>
          </>
        )}
      </main>
    </IntelligenceLayout>
  );
}
